import { MapPin, ShieldCheck, Lock, FileText, AlertTriangle, ClipboardCheck, Layers, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLang } from "@/lib/i18n";

const Indicator = ({ icon: Icon, label, value, tone }) => (
  <div data-testid="preview-indicator" className="flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2">
    <Icon className={`h-4 w-4 ${tone || "text-primary"}`} />
    <div className="leading-tight">
      <p className="font-mono text-[9px] uppercase tracking-[0.14em] text-muted-foreground">{label}</p>
      <p className="text-sm font-semibold text-foreground">{value}</p>
    </div>
  </div>
);

export const PropertyPreview = ({ report, onGetReport }) => {
  const { t } = useLang();
  const arr = (v) => (Array.isArray(v) ? v : null);
  const permits = arr(report.permits);
  const violations = arr(report.violations);
  const inspections = arr(report.inspections);
  const openPermits = permits ? permits.filter((p) => /open|active|issued/i.test(p.status || "")).length : null;
  const sources = [...(report.official_sources || []), ...(report.sources || [])];
  const srcCount = new Set(sources.map((s) => s.name || s.url || s.field).filter(Boolean)).size;

  return (
    <div data-testid="property-preview" className="mx-auto max-w-4xl rounded-2xl border border-border bg-card p-6 shadow-sm sm:p-8">
      <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.16em] text-emerald-600 dark:text-emerald-400">
        <ShieldCheck className="h-3.5 w-3.5" />{t("preview.badge")}
      </span>
      <div className="mt-4 flex items-start gap-2">
        <MapPin className="mt-1 h-5 w-5 shrink-0 text-primary" />
        <div>
          <h2 className="font-serif text-2xl font-semibold text-foreground" data-testid="preview-address">{report.site_address || report.address}</h2>
          <p className="mt-0.5 text-sm text-muted-foreground">{report.jurisdiction || report.municipality}</p>
        </div>
      </div>

      <div className="mt-5 grid gap-3 sm:grid-cols-2">
        <div className="rounded-lg bg-secondary/40 px-3 py-2"><p className="font-mono text-[9px] uppercase tracking-[0.14em] text-muted-foreground">Folio / Parcel ID</p><p className="font-mono text-sm font-semibold text-foreground">{report.folio || "—"}</p></div>
        <div className="rounded-lg bg-secondary/40 px-3 py-2"><p className="font-mono text-[9px] uppercase tracking-[0.14em] text-muted-foreground">Property type</p><p className="text-sm font-semibold text-foreground">{report.property_use || "—"}</p></div>
        <div className="rounded-lg bg-secondary/40 px-3 py-2"><p className="font-mono text-[9px] uppercase tracking-[0.14em] text-muted-foreground">Zoning</p><p className="text-sm font-semibold text-foreground">{report.zoning || "—"}</p></div>
        <div className="rounded-lg bg-secondary/40 px-3 py-2"><p className="font-mono text-[9px] uppercase tracking-[0.14em] text-muted-foreground">Land use</p><p className="text-sm font-semibold text-foreground">{report.land_use || "—"}</p></div>
      </div>

      <p className="mt-6 mb-2 font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-accent">{t("preview.indicators")}</p>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        {permits !== null && <Indicator icon={FileText} label="Permits Found" value={permits.length} />}
        {openPermits !== null && <Indicator icon={FileText} label="Open Permits" value={openPermits} tone="text-amber-500" />}
        {violations !== null && <Indicator icon={AlertTriangle} label="Violations Found" value={violations.length} tone={violations.length ? "text-destructive" : "text-emerald-500"} />}
        {inspections !== null && <Indicator icon={ClipboardCheck} label="Inspection Records" value={inspections.length} />}
        {report.zoning && <Indicator icon={Layers} label="Zoning" value={report.zoning} />}
        {srcCount > 0 && <Indicator icon={Building2} label="Official Sources" value={srcCount} />}
      </div>

      <div className="mt-7 rounded-xl border border-primary/25 bg-primary/5 p-5 text-center">
        <p className="flex items-center justify-center gap-1.5 font-serif text-lg font-semibold text-foreground"><Lock className="h-4 w-4 text-primary" />{t("preview.locked")}</p>
        <p className="mt-1 text-sm text-muted-foreground">{t("preview.support")}</p>
        <Button size="lg" className="mt-4" data-testid="get-free-report-btn" onClick={onGetReport}>{t("preview.cta")}</Button>
        <p className="mt-2 text-[11px] text-muted-foreground">{t("auth.noCard")}</p>
      </div>
    </div>
  );
};
